/**
 * MODULE: domain/pages/frontend/utils/workspaceId.ts
 * PURPOSE: Validate and resolve workspace_id before pages-service calls (backend expects UUID).
 * DEPENDENCIES: domain/workspace/frontend/api/workspaceApi (MOCK_WORKSPACE_IDS)
 * PUBLIC_API: isWorkspaceUuid, resolvePagesWorkspaceId, INVALID_PAGES_WORKSPACE_MESSAGE
 * SIDE_EFFECTS: none
 * AGENT SAFETY: 🟢 SAFE
 * RECENT: 2026-05-24 [2.2.1137] — legacy UAT slug ids (ws-acme-sales-hq) map to stable mock UUIDs.
 */

import { MOCK_WORKSPACE_IDS } from '../../../workspace/frontend/api/workspaceApi';

const WORKSPACE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Pre-UUID mock workspace ids still present in bookmarks and localStorage. */
const LEGACY_MOCK_WORKSPACE_ID_TO_UUID: Record<string, string> = {
  'ws-acme-sales-hq': MOCK_WORKSPACE_IDS.acmeSalesHq,
  'ws-enterprise-accounts': MOCK_WORKSPACE_IDS.enterpriseAccounts,
  'ws-smb-pipeline': MOCK_WORKSPACE_IDS.smbPipeline,
};

/** True when id is a UUID accepted by pages-service query params. */
export function isWorkspaceUuid(id: string | undefined | null): boolean {
  if (!id?.trim()) return false;
  return WORKSPACE_UUID_RE.test(id.trim());
}

/**
 * Normalize route param or request body workspace_id for pages API.
 * Maps legacy mock slug ids to stable UUIDs; returns null when unusable.
 */
export function resolvePagesWorkspaceId(id: string | undefined | null): string | null {
  const trimmed = id?.trim();
  if (!trimmed) return null;
  if (isWorkspaceUuid(trimmed)) return trimmed;
  return LEGACY_MOCK_WORKSPACE_ID_TO_UUID[trimmed] ?? null;
}

export const INVALID_PAGES_WORKSPACE_MESSAGE =
  'This workspace ID is not valid for Pages. Open Pages from the workspace list and try again.';
